import CurrentRiskBadge from '../../components/CurrentRiskBadge';
import FacilityDocuments from '../../components/FacilityDocuments';
import usePageTitle from '../../hooks/usePageTitle';
import React, { useCallback, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2,
  Search,
  MapPin,
  User,
  ArrowRight,
} from 'lucide-react';
import { Facility, User as ApiUser } from '../../types';
import { facilitiesService, usersService } from '../../services';
import { useAuth } from '../../contexts/AuthContext';
import { usePortalScope } from '../../hooks/usePortalScope';
import './ClientPortalFacilities.css';

const ClientPortalFacilities: React.FC = () => {
  usePageTitle('Facilities');
  const { loading: authLoading } = useAuth();
  const { clientId, managerId, scopeFilter } = usePortalScope();
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [users, setUsers] = useState<ApiUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const loadData = useCallback(async () => {
    if (!scopeFilter) {
      setFacilities([]);
      setUsers([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      void usersService.getUsers().then(setUsers).catch(() => {});
      const facilitiesData = await facilitiesService.getFacilities(scopeFilter);

      setFacilities(facilitiesData);
    } catch (err) {
      console.error('Error loading facilities:', err);
    } finally {
      setLoading(false);
    }
  }, [clientId, managerId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!authLoading) {
      loadData();
    }
  }, [authLoading, loadData]);

  const filteredFacilities = facilities.filter((facility) =>
    facility.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    facility.address.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="client-facilities-loading">
        <div className="loading-spinner" />
        <p>Loading your facilities...</p>
      </div>
    );
  }

  if (!clientId && !managerId) {
    return (
      <div className="client-facilities-loading">
        <p>No account scope found. Please contact your administrator.</p>
      </div>
    );
  }

  return (
    <div className="client-portal-facilities">
      <div className="page-header">
        <div className="header-content">
          <h1>
            <Building2 size={28} />
            My Facilities
          </h1>
          <p>View and manage your facilities</p>
        </div>
      </div>

      {/* Search */}
      <div className="facilities-toolbar">
        <div className="search-container">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search facilities by name or address..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="results-count">
          {filteredFacilities.length} of {facilities.length} facilities
        </div>
      </div>

      {/* Facilities Grid */}
      <div className="facilities-grid">
        {filteredFacilities.length === 0 ? (
          <div className="empty-state">
            <Building2 size={48} />
            <h3>No Facilities Found</h3>
            <p>
              {facilities.length === 0
                ? 'You have no facilities assigned to your account.'
                : 'No facilities match your search.'}
            </p>
          </div>
        ) : (
          filteredFacilities.map((facility) => {
            const manager = users.find((u) => u.id === facility.manager_id);

            return (
              <div key={facility.id} className="facility-card">
                <div className="facility-header">
                  <div className="facility-icon">
                    <Building2 size={24} />
                  </div>
                  <div className="facility-title">
                    <h3>{facility.name}</h3><CurrentRiskBadge risk={facility.risk_summary?.current_risk} />
                    <div className="facility-address">
                      <MapPin size={14} />
                      {facility.address}
                    </div>
                  </div>
                </div>

                <div className="facility-details">
                  {manager && (
                    <div className="detail-item">
                      <User size={16} />
                      <span>
                        <strong>Manager:</strong> {manager.name}
                      </span>
                    </div>
                  )}
                </div>

                <details><summary>Documents &amp; images</summary><FacilityDocuments facilityId={facility.id} /></details>
                <div className="facility-footer">
                  <Link to={`/portal/facilities/${facility.id}`} className="view-link">
                    View Details
                    <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ClientPortalFacilities;
